export const DEFAULT_MAX_PREVIEW_FILE_SIZE_BYTES = 30000000; // 30mb

export type RenderPreviewAvailability = {
  fileAvailable: boolean;
  validFileSize: boolean;
};

/**
 * Checks if file from url is available for preview using HEAD
 * request. Use this before fetching any files
 *
 * @param { string } url
 * @param { number? } maxContentLengthBytes
 * @returns Promise<RenderPreviewAvailability>
 */
export const getRenderPreviewAvailability = async (
  url: string,
  maxContentLengthBytes?: number
): Promise<RenderPreviewAvailability> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    const contentLength = Number(response?.headers?.get('content-length'));
    return {
      fileAvailable: !!contentLength,
      validFileSize:
        !!contentLength &&
        contentLength > 0 &&
        contentLength <= (maxContentLengthBytes || DEFAULT_MAX_PREVIEW_FILE_SIZE_BYTES),
    };
  } catch {
    return { fileAvailable: false, validFileSize: false };
  }
};

/**
 * Gets text version of a code file from given url
 *
 * @param { string } url
 * @returns Promise<string | null>
 */
export const getCodeBlockText = async (url: string) => {
  try {
    const response = await fetch(url, { method: 'GET' });
    return response.text();
  } catch {
    return null;
  }
};

const getFileExtension = (url: string) => {
  return url.split('.').pop()?.toLowerCase();
};

/**
 * Gives the language type of a given file url if it's
 * supported; undefined if not supported
 *
 * @param { string } url
 * @returns string | undefined
 */
export const getLanguageTypeFromFileUrl = (url: string) => {
  const fileExtension = getFileExtension(url);
  return fileExtension
    ? {
        txt: 'text',
        py: 'python',
        r: 'r',
        js: 'javascript',
        json: 'json',
        java: 'java',
        log: 'text',
      }?.[fileExtension]
    : undefined;
};
